import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Snackbar from '@mui/material/Snackbar'
import { NotificationProviderProps, Notification, NotificationContextProps } from './types'
import { useTheme } from '@mui/material/styles'
import Slide, { SlideProps } from '@mui/material/Slide'
import { Alert } from '@mui/material'
import { ThumbsUp } from 'react-feather'
import { alertStyles } from './Notification.styles'
import { ThumbDown } from '@mui/icons-material'

const AUTO_HIDE = 3000

const NotificationContext = createContext({} as NotificationContextProps)

function SlideTransition(props: SlideProps) {
	return <Slide {...props} direction="left" />
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
	const { palette } = useTheme()
	const [open, setOpen] = useState(false)
	const [notification, setNotification] = useState<Notification>({
		error: '',
		success: '',
		onClose(): void {},
		timeout: 0,
	})

	useEffect(() => {
		const timer = setTimeout(() => setOpen(false), notification.timeout || AUTO_HIDE)
		return () => clearTimeout(timer)
	}, [notification])

	const addMessage = useCallback((newNotification: Notification) => {
		setNotification(newNotification)
		setOpen(true)
	}, [])

	const cleanNotifications = useCallback(() => {
		setOpen(false)
		setNotification({} as Notification)
	}, [])

	const data = useMemo(
		() => ({
			addMessage,
			cleanNotifications,
		}),
		[addMessage, cleanNotifications],
	)

	if (notification.error && notification.success) return null

	return (
		<NotificationContext.Provider value={data}>
			{children}
			<Snackbar
				open={open}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				autoHideDuration={notification.timeout || AUTO_HIDE}
				onClose={notification.onClose}
				TransitionComponent={SlideTransition}
			>
				<Alert
					onClose={notification.onClose}
					variant="filled"
					icon={!!notification.success ? <ThumbsUp /> : <ThumbDown />}
					sx={alertStyles(palette, !!notification.success)}
				>
					{notification.success || notification.error}
				</Alert>
			</Snackbar>
		</NotificationContext.Provider>
	)
}

export const useNotification = () => useContext(NotificationContext)
